import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { specialAwardsService } from './specialAwards.service';
import { getCategories } from './categories.services';

// Types
export interface StudentAward {
  studentId: string;
  projectId: string;
  name: string;
  phoneticName: string;
  school: string;
  award: string; // Category name or special award name
  placement: string; // "1st Place", "2nd Place", etc. or "Special Award"
  score: number; // Average score from judges (0 for special awards)
  isSpecialAward?: boolean; // Flag to indicate if this is a special award
}

export interface ReportStatus {
  fairId: string;
  isPublished: boolean;
  publishedAt?: Date;
  publishedBy?: string;
}

export interface ReportData {
  id: string;
  fairId: string;
  studentAwards: StudentAward[];
  createdAt: Date;
  updatedAt: Date;
  generatedBy: string;
}

// Use provided phonetic name when available; otherwise return empty so UI can show fallback
function generatePhoneticName(_name: string, phonetic?: string): string {
  const cleaned = phonetic?.trim();
  return cleaned && cleaned.length > 0 ? cleaned : '';
}

// Helper function to calculate placements based on scores
function calculatePlacements(
  projectsWithScores: Array<{
    projectId: string;
    studentId: string;
    name: string;
    phoneticName: string;
    school: string;
    category: string;
    averageScore: number;
  }>
): StudentAward[] {
  // Group by category
  const byCategory: Record<string, typeof projectsWithScores> = {};
  projectsWithScores.forEach((project) => {
    if (!byCategory[project.category]) {
      byCategory[project.category] = [];
    }
    byCategory[project.category].push(project);
  });

  const awards: StudentAward[] = [];

  // For each category, sort by score and assign placements
  Object.entries(byCategory).forEach(([category, projects]) => {
    // Sort by average score descending
    const sorted = [...projects].sort(
      (a, b) => b.averageScore - a.averageScore
    );

    // Handle ties by grouping projects with the same score
    let currentIndex = 0;
    while (currentIndex < sorted.length) {
      const currentScore = sorted[currentIndex].averageScore;
      const tiedProjects: typeof sorted = [];

      // Collect all projects with the same score
      while (
        currentIndex < sorted.length &&
        Math.abs(sorted[currentIndex].averageScore - currentScore) < 0.01
      ) {
        tiedProjects.push(sorted[currentIndex]);
        currentIndex++;
      }

      // Determine placement based on the starting index
      const placementIndex = currentIndex - tiedProjects.length;
      let placement = '';
      if (placementIndex === 0) placement = '1st Place';
      else if (placementIndex === 1) placement = '2nd Place';
      else if (placementIndex === 2) placement = '3rd Place';
      else if (placementIndex < 6) placement = 'Honorable Mention';
      else placement = 'Participation';

      // Assign the same placement to all tied projects
      tiedProjects.forEach((project) => {
        awards.push({
          studentId: project.studentId,
          projectId: project.projectId,
          name: project.name,
          phoneticName: project.phoneticName,
          school: project.school,
          award: category,
          placement,
          score: project.averageScore,
          isSpecialAward: false, // Explicitly set to false for standard awards
        });
      });
    }
  });

  return awards;
}

// Service functions
export const reportingService = {
  /**
   * Generate report data for a fair based on judge scores
   */
  async generateReportData(
    fairId: string,
    userId: string
  ): Promise<ReportData> {
    try {
      // 1. Get all categories to map IDs to names
      const categories = await getCategories('');
      const categoryMap = new Map<string, string>();
      categories.forEach((cat) => {
        categoryMap.set(cat.id, cat.name);
      });

      // 2. Get all projects for this fair
      const projectsRef = collection(db, 'projects');
      const projectsQuery = query(projectsRef, where('fairId', '==', fairId));
      const projectsSnapshot = await getDocs(projectsQuery);

      // 3. Get all project scores
      const scoresRef = collection(db, 'projectScores');

      // Build project data with scores
      const projectsWithScores = [];

      for (const projectDoc of projectsSnapshot.docs) {
        const projectData = projectDoc.data();
        const projectId = projectDoc.id;

        // Get scores for this project
        const scoresQuery = query(
          scoresRef,
          where('projectId', '==', projectId)
        );
        const scoresSnapshot = await getDocs(scoresQuery);

        const scores = scoresSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            // Handle both old format (direct score) and new format (score field)
            return typeof data.score === 'number' ? data.score : null;
          })
          .filter((score): score is number => score !== null);

        const averageScore =
          scores.length > 0
            ? scores.reduce((sum, score) => sum + score, 0) / scores.length
            : 0;

        // Skip projects with no scores
        if (scores.length === 0) continue;

        // Get student data for each project member
        const memberIds = projectData.memberIds || [];

        // Get category - handle both array and single value, and convert ID to name
        const categoryId = Array.isArray(projectData.categories)
          ? projectData.categories[0]
          : projectData.category || projectData.categories || null;

        // Convert category ID to name, or use 'General' as fallback
        const category =
          categoryId && categoryMap.has(categoryId)
            ? categoryMap.get(categoryId)!
            : categoryId || 'General';

        for (const memberId of memberIds) {
          const userDoc = await getDoc(doc(db, 'users', memberId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            projectsWithScores.push({
              projectId,
              studentId: memberId,
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              phoneticName: generatePhoneticName(
                `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                userData.phoneticName
              ),
              school: userData.school || '',
              category: category,
              averageScore,
            });
          }
        }
      }

      // 3. Calculate placements
      const studentAwards = calculatePlacements(projectsWithScores);

      // 4. Add special awards
      try {
        const specialAwards =
          await specialAwardsService.getSpecialAwardsByFair(fairId);

        for (const specialAward of specialAwards) {
          // Track which projects have already been added for this award to avoid duplicates
          const addedProjectIds = new Set<string>();
          const addedStudentIds = new Set<string>();

          // Add awards for each project - group all students in the project together
          for (const projectId of specialAward.projectIds || []) {
            if (addedProjectIds.has(projectId)) continue;

            try {
              const projectDoc = await getDoc(doc(db, 'projects', projectId));
              if (projectDoc.exists()) {
                const projectData = projectDoc.data();
                const memberIds = projectData.memberIds || [];

                // Collect all students for this project
                const projectStudents: Array<{
                  studentId: string;
                  name: string;
                  phoneticName: string;
                  school: string;
                }> = [];

                for (const memberId of memberIds) {
                  if (!addedStudentIds.has(memberId)) {
                    const userDoc = await getDoc(doc(db, 'users', memberId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      projectStudents.push({
                        studentId: memberId,
                        name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                        phoneticName: generatePhoneticName(
                          `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                          userData.phoneticName
                        ),
                        school: userData.school || '',
                      });
                      addedStudentIds.add(memberId);
                    }
                  }
                }

                // Create one entry per student but with project name stored in a way that allows grouping
                // We'll use the projectId to group them in the display
                for (const student of projectStudents) {
                  studentAwards.push({
                    studentId: student.studentId,
                    projectId: projectId,
                    name: student.name,
                    phoneticName: student.phoneticName,
                    school: student.school,
                    award: specialAward.awardName,
                    placement: 'Special Award',
                    score: 0,
                    isSpecialAward: true,
                  });
                }

                addedProjectIds.add(projectId);
              }
            } catch (error) {
              console.error(
                `Error processing project ${projectId} for special award:`,
                error
              );
            }
          }

          // Add awards for individual students (if not already added via project)
          // These are students selected individually, not as part of a project
          for (const studentId of specialAward.studentIds || []) {
            if (!addedStudentIds.has(studentId)) {
              try {
                const userDoc = await getDoc(doc(db, 'users', studentId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  // Try to find the project for this student
                  const projectsQuery = query(
                    collection(db, 'projects'),
                    where('memberIds', 'array-contains', studentId),
                    where('fairId', '==', fairId)
                  );
                  const projectsSnapshot = await getDocs(projectsQuery);
                  const projectId = projectsSnapshot.docs[0]?.id || '';

                  studentAwards.push({
                    studentId: studentId,
                    projectId: projectId,
                    name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                    phoneticName: generatePhoneticName(
                      `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                      userData.phoneticName
                    ),
                    school: userData.school || '',
                    award: specialAward.awardName,
                    placement: 'Special Award',
                    score: 0,
                    isSpecialAward: true,
                  });
                  addedStudentIds.add(studentId);
                }
              } catch (error) {
                console.error(
                  `Error processing student ${studentId} for special award:`,
                  error
                );
              }
            }
          }
        }
      } catch (error) {
        console.error('Error adding special awards to report:', error);
        // Continue even if special awards fail to load
      }

      // 5. Save to Firestore
      const reportDataRef = doc(db, 'reportData', fairId);
      const reportData: Omit<ReportData, 'id'> = {
        fairId,
        studentAwards,
        createdAt: new Date(),
        updatedAt: new Date(),
        generatedBy: userId,
      };

      await setDoc(reportDataRef, {
        ...reportData,
        createdAt: Timestamp.fromDate(reportData.createdAt),
        updatedAt: Timestamp.fromDate(reportData.updatedAt),
      });

      return { id: fairId, ...reportData };
    } catch (error) {
      console.error('Error generating report data:', error);
      throw error;
    }
  },

  /**
   * Publish a report (make it visible to the public)
   */
  async publishReport(fairId: string, userId: string): Promise<void> {
    try {
      const statusRef = doc(db, 'reportStatuses', fairId);
      const statusData: ReportStatus = {
        fairId,
        isPublished: true,
        publishedAt: new Date(),
        publishedBy: userId,
      };

      await setDoc(statusRef, {
        ...statusData,
        publishedAt: Timestamp.fromDate(statusData.publishedAt!),
      });
    } catch (error) {
      console.error('Error publishing report:', error);
      throw error;
    }
  },

  /**
   * Unpublish a report (hide from public)
   */
  async unpublishReport(fairId: string): Promise<void> {
    try {
      const statusRef = doc(db, 'reportStatuses', fairId);
      await updateDoc(statusRef, {
        isPublished: false,
        publishedAt: null,
        publishedBy: null,
      });
    } catch (error) {
      console.error('Error unpublishing report:', error);
      throw error;
    }
  },

  /**
   * Get report status for a fair
   */
  async getReportStatus(fairId: string): Promise<ReportStatus | null> {
    try {
      const statusRef = doc(db, 'reportStatuses', fairId);
      const statusDoc = await getDoc(statusRef);

      if (!statusDoc.exists()) {
        return null;
      }

      const data = statusDoc.data();
      return {
        fairId: data.fairId,
        isPublished: data.isPublished || false,
        publishedAt: data.publishedAt?.toDate(),
        publishedBy: data.publishedBy,
      };
    } catch (error) {
      console.error('Error getting report status:', error);
      throw error;
    }
  },

  /**
   * Subscribe to report status changes
   */
  subscribeToReportStatus(
    fairId: string,
    callback: (status: ReportStatus | null) => void
  ): () => void {
    const statusRef = doc(db, 'reportStatuses', fairId);

    return onSnapshot(statusRef, (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.data();
      callback({
        fairId: data.fairId,
        isPublished: data.isPublished || false,
        publishedAt: data.publishedAt?.toDate(),
        publishedBy: data.publishedBy,
      });
    });
  },

  /**
   * Get report data for a fair
   */
  async getReportData(fairId: string): Promise<ReportData | null> {
    try {
      const reportRef = doc(db, 'reportData', fairId);
      const reportDoc = await getDoc(reportRef);

      if (!reportDoc.exists()) {
        return null;
      }

      const data = reportDoc.data();
      return {
        id: reportDoc.id,
        fairId: data.fairId,
        studentAwards: data.studentAwards || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        generatedBy: data.generatedBy || '',
      };
    } catch (error) {
      console.error('Error getting report data:', error);
      throw error;
    }
  },

  /**
   * Subscribe to all published reports
   */
  subscribeToPublishedReports(
    callback: (reports: Array<{ fairId: string; data: ReportData }>) => void
  ): () => void {
    // Subscribe to published statuses
    const statusesRef = collection(db, 'reportStatuses');
    const publishedQuery = query(statusesRef, where('isPublished', '==', true));

    return onSnapshot(publishedQuery, async (snapshot) => {
      const publishedFairIds = snapshot.docs.map(
        (doc) => doc.data().fairId as string
      );

      // Fetch report data for each published fair
      const reports = await Promise.all(
        publishedFairIds.map(async (fairId) => {
          const data = await this.getReportData(fairId);
          return { fairId, data: data! };
        })
      );

      callback(reports.filter((r) => r.data !== null));
    });
  },

  /**
   * Get all report statuses
   */
  async getAllReportStatuses(): Promise<ReportStatus[]> {
    try {
      const statusesRef = collection(db, 'reportStatuses');
      const snapshot = await getDocs(statusesRef);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          fairId: data.fairId,
          isPublished: data.isPublished || false,
          publishedAt: data.publishedAt?.toDate(),
          publishedBy: data.publishedBy,
        };
      });
    } catch (error) {
      console.error('Error getting report statuses:', error);
      throw error;
    }
  },

  /**
   * Check if a fair is completed and eligible for report publishing
   */
  async isFairEligibleForPublishing(fairId: string): Promise<boolean> {
    try {
      const fairRef = doc(db, 'fairs', fairId);
      const fairDoc = await getDoc(fairRef);

      if (!fairDoc.exists()) {
        return false;
      }

      const fairData = fairDoc.data();
      return fairData.status === 'completed';
    } catch (error) {
      console.error('Error checking fair eligibility:', error);
      return false;
    }
  },
};
